import { AuthHelper } from "@/components/pages/Auth/AuthHelper";
import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import { OutlineButton } from "@/components/StoreComponents/StoreButton";
import { CheckoutToggle } from "@/components/StoreComponents/StoreToggle";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch } from "@/redux/hooks";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "@/utils/StringUtils";
import { Box, useTheme } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CheckoutInputV2 } from "../../../CheckoutInputV2";
import { CheckoutButton } from "./CheckoutButton";
import { SuccessStatusIcon } from "./SuccessStatusIcon";

const MobileCheckoutSignIn = () => {
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const lastSubmittedCodeRef = useRef<string>("");

  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");

  const [agreedToText, setAgreedToText] = useState<boolean>(false);
  const hasOtpSession = !!otpSession?.username;

  useEffect(() => {
    // Check if the phone number entered is different from the one in the session; if so, clear the session
    if (
      hasOtpSession &&
      AuthHelper.getEmailForPhoneNumber(phoneNumber) !== otpSession.username
    ) {
      setOtpSession(undefined);
    }
  }, [hasOtpSession, otpSession?.username, phoneNumber]);

  const {
    signIn,
    submittingSignIn,
    loadingProfile,
    verifyingOtp,
    otpError,
    resendCooldown,
    verifyOtp,
    resendOtp,
    clearOtpError,
  } = useContext(UserLoginContext);

  const submitPhoneNumber = () => {
    const phoneNumberInput = formatPhone(phoneNumber, "e164");
    signIn(phoneNumberInput, false, (_, session) => {
      setOtpSession(session);
    });
  };

  const submitOTP = useCallback(
    (otp: string) => {
      verifyOtp(otp, otpSession, () => {
        dispatch(reloadProfile());
      });
    },
    [dispatch, otpSession, verifyOtp]
  );

  useEffect(() => {
    const OTP_EXPECTED_LENGTH = 6;
    if (
      verificationCode.length === OTP_EXPECTED_LENGTH &&
      verificationCode !== lastSubmittedCodeRef.current
    ) {
      lastSubmittedCodeRef.current = verificationCode;
      submitOTP(verificationCode);
    }
  }, [submitOTP, verificationCode]);

  const resendOTP = () => {
    resendOtp(phoneNumber, false, (session) => {
      setOtpSession(session);
    });
  };

  const Terms = () => {
    return (
      <Box
        display="flex"
        gap="8px"
        alignItems="flex-start"
        width="100%"
        fontSize="12px"
        lineHeight="18px"
        fontWeight="500"
      >
        <CheckoutToggle
          disabled={
            submittingSignIn || verifyingOtp || loadingProfile || !!otpSession
          }
          checked={agreedToText}
          onClick={() => setAgreedToText(!agreedToText)}
        />
        To continue, consent to receiving text messages about orders and
        marketing offers.
      </Box>
    );
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: "16.3px",
          [theme.breakpoints.up("md")]: {
            ".mobileOnly": {
              display: "none",
            },
          },
        }}
      >
        <CheckoutInputV2
          label="Phone Number"
          name="phoneNumber"
          autoComplete="tel"
          value={phoneNumber}
          style={{
            minWidth: "300px",
          }}
          onChange={(e) => {
            const value = e.target.value;
            setPhoneNumber(value?.trim());
            // Clear any previous errors if needed
          }}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            inputProps: {
              format: getPhoneNumberMasking(phoneNumber || ""),
            },
          }}
          sx={{ flex: 1 }}
        />

        {otpSession ? (
          <>
            <CheckoutInputV2
              label="Verification Code"
              name="verificationCode"
              autoComplete="one-time-code"
              value={verificationCode}
              disabled={
                !otpSession ||
                submittingSignIn ||
                verifyingOtp ||
                loadingProfile
              }
              style={{
                minWidth: "300px",
              }}
              inputStyle={{
                color: otpError ? theme.palette.error.main : "inherit",
              }}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                clearOtpError();
              }}
              sx={{ flex: 1 }}
              endButton={
                otpError || loadingProfile ? (
                  <SuccessStatusIcon
                    success={!otpError}
                    width={20}
                    height={20}
                  />
                ) : null
              }
            />
            <Terms />
            <OutlineButton
              disableMaxWidth
              onClick={() => {
                setVerificationCode("");
                clearOtpError();
                resendOTP();
              }}
              disabled={resendCooldown > 0 || submittingSignIn || verifyingOtp}
            >
              Resend Code
            </OutlineButton>
          </>
        ) : (
          <>
            <Terms />
            <CheckoutButton
              onClick={() => {
                submitPhoneNumber();
              }}
              disabled={
                submittingSignIn ||
                verifyingOtp ||
                !validatePhone(phoneNumber) ||
                !agreedToText
              }
            >
              {submittingSignIn ? "Sending" : "Send code"}
            </CheckoutButton>
          </>
        )}
      </Box>
    </div>
  );
};

export default MobileCheckoutSignIn;
